// T008: Create QuickPostForm component skeleton
// T011: Implement textarea, Post button, and validation
// T012: Add loading state display during posting
// T013: Add error message display for post failures
// T022: Add image file input and preview display
// T023: Implement image removal functionality
// T025: Add upload progress indicator
// T026: Implement image upload failure dialog with retry/skip options

import { useState, useCallback, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, Send, AlertCircle, ImagePlus } from 'lucide-react'
import { useQuickPost } from '@/hooks/useQuickPost'
import { QuickPostImagePreview } from './QuickPostImagePreview'
import { QUICK_POST_CONSTANTS } from '@/types/quickPost'
import type { QuickPostInput } from '@/types/quickPost'

type ImageUploadError = {
  message: string
  file: File
}

interface QuickPostFormProps {
  onSuccess?: () => void
}

export function QuickPostForm({ onSuccess }: QuickPostFormProps = {}) {
  const [body, setBody] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const [imageUploadError, setImageUploadError] = useState<ImageUploadError | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { status, error, uploadProgress, submit, reset } = useQuickPost()

  const isUploading = status === 'uploading'
  const isPosting = status === 'posting'
  const isBusy = isUploading || isPosting
  const isDisabled = !body.trim() || isBusy

  // Cleanup preview URL on unmount or when image changes
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl)
      }
    }
  }, [imagePreviewUrl])

  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!QUICK_POST_CONSTANTS.SUPPORTED_IMAGE_TYPES.includes(file.type as typeof QUICK_POST_CONSTANTS.SUPPORTED_IMAGE_TYPES[number])) {
      setImageUploadError({
        message: 'Unsupported image format. Use JPEG, PNG, GIF, or WebP.',
        file,
      })
      return
    }

    // Validate file size
    if (file.size > QUICK_POST_CONSTANTS.IMAGE_MAX_SIZE) {
      setImageUploadError({
        message: 'Image size must be less than 10MB.',
        file,
      })
      return
    }

    // Clear any previous error
    setImageUploadError(null)

    // Revoke previous preview URL
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl)
    }

    // Set new image
    setImageFile(file)
    setImagePreviewUrl(URL.createObjectURL(file))

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [imagePreviewUrl])

  const handleImageRemove = useCallback(() => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl)
    }
    setImageFile(null)
    setImagePreviewUrl(null)
    setImageUploadError(null)
  }, [imagePreviewUrl])

  const handleSubmit = useCallback(async () => {
    if (isDisabled) return

    const input: QuickPostInput = {
      body: body.trim(),
      image: imageFile || undefined,
    }

    const result = await submit(input)

    if (result.success) {
      // T014: Form reset handled here
      setBody('')
      handleImageRemove()
      reset()
      onSuccess?.()
    } else if (result.imageUploadFailed && imageFile) {
      // T026: Image upload failed - show retry/skip dialog
      setImageUploadError({
        message: result.error || 'Image upload failed',
        file: imageFile,
      })
    }
  }, [body, imageFile, isDisabled, submit, reset, handleImageRemove, onSuccess])

  const handleRetryWithoutImage = useCallback(async () => {
    setImageUploadError(null)
    handleImageRemove()

    // Submit without image
    const input: QuickPostInput = { body: body.trim() }
    const result = await submit(input)

    if (result.success) {
      setBody('')
      reset()
    }
  }, [body, submit, reset, handleImageRemove])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Submit on Cmd/Ctrl + Enter (T031)
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  const handleImageButtonClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="space-y-3">
        {/* Textarea for post content */}
        <Textarea
          placeholder="What's happening?"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isBusy}
          className="min-h-[100px] max-h-[40vh] resize-none border-0 bg-transparent p-0 text-base focus-visible:ring-0 overflow-y-auto"
          aria-label="Post content"
        />

        {/* Image preview (T022) */}
        {imagePreviewUrl && (
          <QuickPostImagePreview
            previewUrl={imagePreviewUrl}
            uploadProgress={uploadProgress}
            isUploading={isUploading}
            onRemove={handleImageRemove}
            disabled={isBusy}
          />
        )}

        {/* Error message display (T013) */}
        {error && !imageUploadError && (
          <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Image upload failure dialog (T026) */}
        {imageUploadError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">Image upload failed</p>
                <p className="mt-1 text-destructive/80">{imageUploadError.message}</p>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleSubmit}
                disabled={isBusy}
              >
                Retry
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRetryWithoutImage}
                disabled={isBusy}
              >
                Post without image
              </Button>
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={QUICK_POST_CONSTANTS.SUPPORTED_IMAGE_TYPES.join(',')}
          onChange={handleImageSelect}
          className="hidden"
          aria-label="Select image"
        />

        {/* Action bar */}
        <div className="flex items-center justify-between border-t pt-3">
          <div className="flex items-center gap-2">
            {/* Image button (T022) */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleImageButtonClick}
              disabled={isBusy || !!imageFile}
              aria-label="Add image"
              className="h-8 w-8"
            >
              <ImagePlus className="h-5 w-5" />
            </Button>

            <div className="text-xs text-muted-foreground">
              {body.length > 0 && `${body.length} characters`}
            </div>
          </div>

          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isDisabled}
            aria-label="Post"
          >
            {isBusy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isUploading ? 'Uploading...' : 'Posting...'}
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Post
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
